import React from 'react';

import { IconTypes } from 'DesignLibrary/atoms/Icon';

/*
--------------------------------------------------------------------------------
These types are copied from the Contentful js package, which added BETA improved
TS support in v10.0.  Their types seem to be a little incorrect, so we've copied
and modified.
*/
interface RichTextDataTarget {
  sys: {
    id: string;
    type: 'Link';
    linkType: 'Entry' | 'Asset';
  };
}

interface RichTextData {
  uri?: string;
  target?: RichTextDataTarget;
}

const nodeTypes = [
  'text',
  'heading-1',
  'heading-2',
  'heading-3',
  'heading-4',
  'heading-5',
  'heading-6',
  'paragraph',
  'hyperlink',
  'entry-hyperlink',
  'asset-hyperlink',
  'unordered-list',
  'ordered-list',
  'list-item',
  'blockquote',
  'hr',
  'embedded-entry-block',
  'embedded-entry-inline',
  'document',
  'table',
  'table-row',
  'table',
  'table-header-cell',
  'table-cell',
];
// Added `document` here
type RichTextNodeType = (typeof nodeTypes)[number];

const marks = ['bold', 'underline', 'code', 'italic'];
type Marks = (typeof marks)[number];
export interface RichTextContent {
  data: RichTextData;
  content?: RichTextContent[];
  marks?: { type: Marks }[];
  value?: string;
  nodeType: RichTextNodeType;
}

export type SectionsWithCustomMessages =
  | 'profile_section'
  | 'health_section'
  | 'hsa'
  | 'review_section'
  | 'benefits_section'
  | 'benefits_section_additional'
  | 'unified_landing_page';

export type SubtextProfileSection = {
  /* Subtext for Integrated users. */
  subtext_integrated?: string;

  /* Subtext for Return users. */
  subtext_return_user?: string;
};

export type SubtextHealthSection = {
  /* Subtext for Return users.  */
  text_welcome?: string;

  /*
  Subtext when a previously selected plan exists. Single-plan variant.
  */
  previous_selected_plan_text?: string;

  /*
  Subtext when a previously selected plan exists. Multiple-plan variant.
  */
  previous_selected_plan_text_alternate?: string;

  /*
  Subtext when plan scores/fits are hidden. Single-plan variant.
  */
  subtext_hide_plan_scoring?: string;

  /*
  Subtext when plan scores/fits are hidden. Multiple-plan variant.
  */
  subtext_hide_plan_scoring_alternate?: string;

  /*
  Text when review mode is enabled.
  */
  text_review_tool?: string;

  /*
  Subtext when review mode is enabled.
  */
  subtext_review_tool?: string;

  /*
  Sorted text show when review mode is enabled.
  */
  sortedtext_review_tool?: string;
};

export type SubtextType = {
  /* Default subtext. Single-plan variant. */
  subtext?: string;

  /* Default subtext. Multiple-plan variant. */
  subtext_alternate?: string;
} & SubtextProfileSection &
  SubtextHealthSection;

export type ContentfulHeader = {
  /* The default page header if Go is selected. */
  text: string;

  /* The default page header if BC is selected. */
  text_alternate?: string;

  /* The page header for return users. */
  text_return_user?: string;

  /* 2nd paragraph text for no deductible modal. */
  para_2?: string;

  /* 2nd paragraph text for no deductible modal for just surest. */
  para_2_surest?: string;

  /* 3rd paragraph text for no deductible modal. */
  para_3?: string;

  /* 3rd paragraph text for no deductible modal additional text. */
  para_3_additional_text?: string;
} & SubtextType;

export type ContentfulColor = 'green' | 'blue' | 'yellow' | 'red' | 'purple';

export type ContentfulCustomMessage = {
  contentful_name: string;
  message_id?: string;
  header?: string;
  text?: RichTextContent;
  color?: ContentfulColor;
  icon?: IconTypes;
  url?: string;
  url_button_text?: string;
};

export interface ContentfulCustomContentBlock {
  contentful_name: string;
  title?: string;
  block_id?: string;
  content?: RichTextContent;
  json_content?: Record<string, unknown>;
  button_text?: string;
  button_link?: string;
  image_url?: string;
  cells?: string[][];
}

export type FormattedTooltipProps = {
  text: string | React.ReactNode;
  title?: string | React.ReactNode;
  underline?: string | React.ReactNode;
  note?: string | React.ReactNode;
};

export interface ContentfulTextNoteType {
  contentful_name?: string;
  plan_year?: string;
  supplemental_plan_type?: string | null;
  short_text?: RichTextContent | null;
  long_text?: RichTextContent | null;
  button_text?: string;
}

export function selectAlexGoNotes(
  notes: ContentfulTextNoteType[] | undefined,
  { planYear, upcomingPlanYear }: { planYear: number | undefined; upcomingPlanYear: number | undefined },
  hasNoSelectionMadeYet: boolean,
): ContentfulTextNoteType[] {
  const planYearNote =
    notes?.find((note) => !!note.plan_year && note.plan_year === planYear?.toString()) ??
    (hasNoSelectionMadeYet
      ? notes?.find((note) => !!note.plan_year && note.plan_year === upcomingPlanYear?.toString())
      : undefined);

  return planYearNote ? [planYearNote] : [];
}
